import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	Input,
} from "@monbits/ui";
import { AxiosError } from "axios";
import { Check } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";
import { Logo } from "src/components/logo";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import authProvider from "src/providers/auth-provider";
import type { AuthProvider } from "src/types";
import { sleep } from "src/utils";
import localStorage from "src/utils/local-storage";

interface LoginState {
	isNeedLogout: boolean;
}

const LoginPage: React.FC = () => {
	const emailId = React.useId();
	const passwordId = React.useId();
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [emailError, setEmailError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const [providersList, setProvidersList] = React.useState<AuthProvider[]>([]);
	const navigate = useNavigate();
	const state: LoginState = useLocation().state;

	const openNotification = (title: string, description?: string) => {
		toast.success(title, {
			description,
			icon: <Check className="h-4 w-4" />,
		});
	};

	const validateEmail = (value: string): boolean => {
		if (!value) {
			setEmailError("Please input your email");
			return false;
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(value)) {
			setEmailError("The input is not valid email");
			return false;
		}
		setEmailError("");
		return true;
	};

	const validatePassword = (value: string): boolean => {
		if (!value) {
			setPasswordError("Please input your password");
			return false;
		}
		setPasswordError("");
		return true;
	};

	const handlePostLogin = async (requires2FA: boolean) => {
		if (requires2FA) {
			navigate("/verify-otp");
			openNotification(
				"Login successful",
				"Please verify with your authenticator app",
			);
			return;
		}

		const status = await authProvider.get2FAStatus();

		if (status.enabled) {
			navigate("/");
			openNotification("Login successful", "Welcome back!");
		} else {
			navigate("/setup-2fa");
			toast.info("Two-factor authentication required", {
				description: "Please set up 2FA to secure your account",
			});
		}
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const isEmailValid = validateEmail(email);
		const isPasswordValid = validatePassword(password);

		if (!isEmailValid || !isPasswordValid) {
			return;
		}

		try {
			setIsFormSubmitting(true);
			const response = await authProvider.createUser({ email, password });
			await handlePostLogin(!!response.requires2FA);
			await sleep(500);
		} catch (error) {
			console.error("error occurred: ", error);
			toast.error("Login failed", {
				description: "Invalid email or password",
			});
		} finally {
			setIsFormSubmitting(false);
		}
	};

	useMount(async () => {
		window.addEventListener("popstate", () =>
			navigate("/login", { replace: true }),
		);

		await authProvider.getCookie();

		if (state?.isNeedLogout) {
			localStorage.remove("merchantId");
		} else {
			try {
				await authProvider.getMe();
				navigate("/");
			} catch (e) {
				if (e instanceof AxiosError && e.response?.status === 401) {
					localStorage.remove("merchantId");
				}
			}
		}

		const availProviders = await authProvider.getProviders();
		setProvidersList(availProviders ?? []);
	});

	const isLoading = providersList.length === 0;

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="flex justify-center mb-8">
					<Logo variant="full" size="xl" />
				</div>

				<SpinWithMask isLoading={isLoading} />

				{!isLoading &&
					providersList.findIndex((item) => item.name === "email") !== -1 && (
						<Card>
							<CardHeader>
								<CardTitle>Sign in to your account</CardTitle>
								<CardDescription>
									Enter your credentials to access the dashboard
								</CardDescription>
							</CardHeader>
							<CardContent>
								<form onSubmit={onSubmit}>
									<FieldGroup>
										<Field>
											<FieldLabel htmlFor={emailId}>Email</FieldLabel>
											<Input
												id={emailId}
												type="email"
												placeholder="m@example.com"
												value={email}
												onChange={(e) => {
													setEmail(e.target.value);
													if (emailError) validateEmail(e.target.value);
												}}
												onBlur={() => validateEmail(email)}
												className={emailError ? "border-destructive" : ""}
												required
											/>
											{emailError && (
												<FieldDescription className="text-destructive">
													{emailError}
												</FieldDescription>
											)}
										</Field>

										<Field>
											<FieldLabel htmlFor={passwordId}>Password</FieldLabel>
											<Input
												id={passwordId}
												type="password"
												placeholder="Enter your password"
												value={password}
												onChange={(e) => {
													setPassword(e.target.value);
													if (passwordError) validatePassword(e.target.value);
												}}
												onBlur={() => validatePassword(password)}
												className={passwordError ? "border-destructive" : ""}
												required
											/>
											{passwordError && (
												<FieldDescription className="text-destructive">
													{passwordError}
												</FieldDescription>
											)}
										</Field>

										<Button
											type="submit"
											disabled={isFormSubmitting}
											className="w-full"
										>
											{isFormSubmitting ? (
												<span className="flex items-center gap-2">
													<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
													Signing in...
												</span>
											) : (
												"Sign in"
											)}
										</Button>

										<div className="text-center">
											<a
												href="/forgot-password"
												className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
											>
												Forgot your password?
											</a>
										</div>
									</FieldGroup>
								</form>
							</CardContent>
						</Card>
					)}
			</div>
		</div>
	);
};

export default LoginPage;
